import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, Gamepad2, Home } from 'lucide-react';
import { WordCard } from '@/components/WordCard';
import { MatchingGame } from '@/components/MatchingGame';
import { SpellingGame } from '@/components/SpellingGame';
import { wordsData, categories } from '@/data/words';

type View = 'home' | 'learn' | 'matching' | 'spelling';

const Index = () => {
  const [currentView, setCurrentView] = useState<View>('home');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  const filteredWords = selectedCategory 
    ? wordsData.filter(word => word.category === selectedCategory)
    : [];

  const handleCategorySelect = (categoryId: string, view: View) => {
    setSelectedCategory(categoryId);
    setCurrentWordIndex(0);
    setCurrentView(view);
  };

  const handleNext = () => {
    if (currentWordIndex < filteredWords.length - 1) {
      setCurrentWordIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentWordIndex > 0) {
      setCurrentWordIndex(prev => prev - 1);
    }
  };

  const goHome = () => {
    setCurrentView('home');
    setSelectedCategory(null);
    setCurrentWordIndex(0);
  };

  // Home view
  if (currentView === 'home') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-6xl font-bold rainbow-text mb-4">
              Fun English Learning
            </h1>
            <p className="text-2xl text-gray-600 mb-8">
              Learn English words with pictures and games!
            </p>
          </div>

          {/* Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {categories.map((category) => (
              <Card key={category.id} className="card-category group">
                <CardContent className="p-6 text-center">
                  <div 
                    className="text-6xl mb-4 group-hover:scale-110 transition-transform duration-300"
                    style={{ color: category.color }}
                  >
                    {category.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-2" style={{ color: category.color }}>
                    {category.name}
                  </h3>
                  <p className="text-gray-600 mb-4">{category.description}</p>
                  
                  <div className="space-y-2">
                    <Button
                      onClick={() => handleCategorySelect(category.id, 'learn')}
                      className="w-full btn-fun"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Learn Words
                    </Button>
                    <Button
                      onClick={() => handleCategorySelect(category.id, 'matching')}
                      variant="outline"
                      className="w-full border-2"
                      style={{ borderColor: category.color, color: category.color }}
                    >
                      <Gamepad2 className="w-4 h-4 mr-2" />
                      Matching Game
                    </Button>
                    <Button
                      onClick={() => handleCategorySelect(category.id, 'spelling')}
                      variant="outline"
                      className="w-full border-2"
                      style={{ borderColor: category.color, color: category.color }}
                    >
                      ✏️ Spelling Game
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Footer */}
          <div className="text-center text-gray-500">
            <p>Click on any category to start learning!</p>
          </div>
        </div>
      </div>
    );
  }

  // Learning view
  if (currentView === 'learn' && filteredWords.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="p-4">
          <Button onClick={goHome} variant="outline" className="mb-4">
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
        </div>
        
        <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
          <WordCard
            word={filteredWords[currentWordIndex]}
            onNext={handleNext}
            onPrevious={handlePrevious}
            currentIndex={currentWordIndex}
            totalWords={filteredWords.length}
          />
        </div>
      </div>
    );
  }

  // Matching game view
  if (currentView === 'matching' && filteredWords.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <MatchingGame words={filteredWords} onBack={goHome} />
      </div>
    );
  }

  // Spelling game view
  if (currentView === 'spelling' && filteredWords.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <SpellingGame words={filteredWords} onBack={goHome} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">Loading...</h1>
      </div>
    </div>
  );
};

export default Index;
